import { ChangeEvent, useState } from "react";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { updateCustomerGroupPrintSets } from "../../../services";
import { CustomerGroup, PrintCopyType } from "../../../types/customer.type";
import { COPY_TYPES } from "../../Customer/Details/printCopyTypes";
import { togglePrintCopy } from "./customerGroups";

const TITLE_SX = { fontWeight: 700 } as const;
const COPY_LIST_SX = { pl: 1 } as const;
const ACTIONS_SX = { p: 2 } as const;
const NO_COPY_SELECTED_ERROR = "เลือกอย่างน้อย 1 สำเนา";

interface CopyTypeCheckboxProps {
  copyType: PrintCopyType;
  label: string;
  checked: boolean;
  disabled: boolean;
  onToggle: (copyType: PrintCopyType) => void;
}

function CopyTypeCheckbox({ copyType, label, checked, disabled, onToggle }: Readonly<CopyTypeCheckboxProps>) {
  const handleChange = () => onToggle(copyType);

  return (
    <FormControlLabel
      control={<Checkbox checked={checked} onChange={handleChange} disabled={disabled} />}
      label={`${copyType} · ${label}`}
    />
  );
}

interface PrintSetsDialogProps {
  group: CustomerGroup;
  onClose: () => void;
  onSaved: () => void;
}

/** Mounted per edit, so every open starts from the group's saved copies. */
export default function PrintSetsDialog({ group, onClose, onSaved }: Readonly<PrintSetsDialogProps>) {
  const [isStandard, setIsStandard] = useState<boolean>(group.default_print_sets === null);
  const [selected, setSelected] = useState<PrintCopyType[]>(group.default_print_sets ?? []);
  const [saving, setSaving] = useState<boolean>(false);

  const nextPrintSets = isStandard ? null : selected;
  const hasNoCopy = !isStandard && selected.length === 0;
  const isUnchanged = JSON.stringify(nextPrintSets) === JSON.stringify(group.default_print_sets);
  const canSave = !hasNoCopy && !isUnchanged;

  const handleToggleStandard = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) =>
    setIsStandard(checked);

  const handleToggleCopy = (copyType: PrintCopyType) =>
    setSelected((current) => togglePrintCopy(current, copyType));

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCustomerGroupPrintSets(group.uuid, { default_print_sets: nextPrintSets });
      onSaved();
    } catch {
      // The axios interceptor shows the server's message; the choice stays for a retry.
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={TITLE_SX}>ชุดพิมพ์เริ่มต้น · {group.name}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            สำเนาที่พิมพ์ในชุดที่ 1 ของร้านขายสดในหมวด {group.code} ที่ยังไม่ได้ตั้งค่าการพิมพ์ของตัวเอง
          </Typography>
          <FormControlLabel
            control={
              <Switch checked={isStandard} onChange={handleToggleStandard} disabled={saving} />
            }
            label="ใช้ค่ามาตรฐาน"
          />
          {!isStandard && (
            <Stack sx={COPY_LIST_SX}>
              {COPY_TYPES.map((copy) => (
                <CopyTypeCheckbox
                  key={copy.key}
                  copyType={copy.key}
                  label={copy.label}
                  checked={selected.includes(copy.key)}
                  disabled={saving}
                  onToggle={handleToggleCopy}
                />
              ))}
              {hasNoCopy && <FormHelperText error>{NO_COPY_SELECTED_ERROR}</FormHelperText>}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={ACTIONS_SX}>
        <Button variant="outlined" onClick={handleClose} disabled={saving}>
          ยกเลิก
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave} loading={saving}>
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
